import { APIService } from '../../../lib/api/APIService';

// Quiz Generator API class extending the base APIService
class QuizGeneratorAPI extends APIService {
  async uploadFiles(specFile, pastPaperFile) {
    const formData = new FormData();
    formData.append('specification', specFile);
    formData.append('past_paper', pastPaperFile);
    
    return this.post('/api/quiz-generation/upload', formData, {
      skipAuth: false,
      isFormData: true
    });
  }

  // userDocumentIds: the uploaded files the quiz is made from (from
  // uploadedDocumentIds), linked to the saved quiz. Sent only when there
  // are some; each must be the student's, or the server answers 422.
  async generateQuiz(cacheKey, numQuestions = 3, topicFocus = '', userDocumentIds = []) {
    const ids = (userDocumentIds || []).filter(Boolean);
    return this.post('/api/quiz-generation/generate_quiz', {
      cache_key: cacheKey,
      num_questions: numQuestions,
      topic_focus: topicFocus,
      ...(ids.length ? { user_document_ids: ids } : {}),
    });
  }

  async getUserContent() {
    return this.get('/api/quiz-generation/user_content');
  }

  async getAvailableExams() {
    return this.get('/api/quiz-generation/available_exams');
  }

  async startExam(examName) {
    return this.post(`/api/quiz-generation/start_exam/${examName}`);
  }

  async getSetPapers(examName) {
    return this.get(`/api/quiz-generation/exams/${examName}/set-papers`);
  }

  async startSetPaper(examName, paperId, catalogId) {
    const id = paperId || catalogId || '';
    const catalog = catalogId || paperId || '';
    const params = new URLSearchParams();
    if (id) params.set('paper_id', id);
    if (catalog && catalog !== id) params.set('catalog_id', catalog);
    const qs = params.toString();
    return this.post(
      `/api/quiz-generation/start_set_paper/${examName}${qs ? `?${qs}` : ''}`,
      { paper_id: id, catalog_id: catalog }
    );
  }

  // The exam's topic papers: one file of practice questions each, published by the
  // content importer. An empty list means there are none yet; a failure rejects.
  async getTopicPapers(examName) {
    return this.get(`/api/quiz-generation/exams/${encodeURIComponent(examName)}/topic-papers`);
  }

  async startTopicPaper(examName, paperId) {
    return this.post(
      `/api/quiz-generation/start_topic_paper/${encodeURIComponent(examName)}`,
      { paper_id: paperId }
    );
  }

  // sessionId / tracking: the admissions session from startExam and its
  // buildQuizTracking payload, recorded after the attempt is saved.
  async submitAttempt(examName, answers, userId = null, campaignData = null, paperId = null, sessionId = null, tracking = null) {
    return this.post('/api/quiz-generation/submit_attempt', {
      exam_name: examName,
      answers: answers,
      user_id: userId,
      campaign_data: campaignData,
      paper_id: paperId || undefined,
      session_id: sessionId || undefined,
      tracking: sessionId && tracking ? tracking : undefined,
    });
  }

  // A custom quiz has no attempt row; its session (from generateQuiz) is its record.
  async submitCustomQuiz(sessionId, tracking) {
    return this.post('/api/quiz-generation/submit_quiz_with_analytics', {
      session_id: sessionId,
      tracking,
    });
  }

  async startPaperAttempt(examName, paperId, { forceNew = false } = {}) {
    return this.post('/api/quiz-generation/paper_attempts/start', {
      exam_name: examName,
      paper_id: paperId,
      force_new: forceNew,
    });
  }

  /** Start or resume two or more parts of one sitting under one sitting id
   * the server picks. Returns { success, sitting_id, attempts: { <paper id>:
   * { attempt_id, paper_id, status, resumed, sitting_id, session_id } } }. */
  async startPaperSitting(examName, paperIds, { forceNew = false } = {}) {
    return this.post('/api/quiz-generation/paper_attempts/start_sitting', {
      exam_name: examName,
      paper_ids: paperIds,
      force_new: forceNew,
    });
  }

  async savePaperAnswers(attemptId, answers) {
    return this.post('/api/quiz-generation/paper_attempts/save', {
      attempt_id: attemptId,
      answers,
    });
  }

  /**
   * BACKEND TODO (not yet implemented — this route does not exist):
   * Persist per-section complete/incomplete progress so a mock-sitting can
   * show/resume section status independently of the answer-save flow above.
   * Frontend sends: exam_name (e.g. "UCAT"), section_key (the module/paper
   * key already used elsewhere for tagging, e.g. pearsonModule / tmuaPaper /
   * paperId — whichever the backend prefers as the stable per-section id),
   * status: 'complete' | 'incomplete'. Called fire-and-forget from the
   * "Back to Exam List" button on the pre-submit review screen (between
   * paper sections), so the response is not otherwise handled by the
   * frontend yet — expect 404 until this route exists.
   */
  async saveSectionProgress(examName, sectionKey, status) {
    return this.post('/api/quiz-generation/save_section_progress', {
      exam_name: examName,
      section_key: sectionKey,
      status,
    });
  }

  // tracking: buildQuizTracking for this part; the backend ends the part's
  // session, which it started with the attempt.
  async completePaperAttempt(attemptId, answers = null, attemptMode = null, tracking = null) {
    return this.post('/api/quiz-generation/paper_attempts/complete', {
      attempt_id: attemptId,
      answers: answers || undefined,
      attempt_mode: attemptMode || undefined,
      tracking: tracking || undefined,
    });
  }

  async pausePaperAttempts(segments = []) {
    return this.post('/api/quiz-generation/paper_attempts/pause', {
      segments,
    });
  }

  async getPaperAttemptStatuses(examName, paperIds = null) {
    return this.post('/api/quiz-generation/paper_attempts/statuses', {
      exam_name: examName,
      paper_ids: paperIds || undefined,
    });
  }

  async getPaperAttemptAnswers(attemptId) {
    return this.get(`/api/quiz-generation/paper_attempts/${attemptId}/answers`);
  }

  async getHealth() {
    return this.get('/api/quiz-generation/health');
  }

  // Every TARA feedback call carries an explicit timeout and both retry
  // guards. 55s sits just inside the platform edge, so the client aborts first
  // and yields a nameable AbortError rather than an opaque 502. AbortError is
  // classified retryable in APIService, so without these flags a raise of
  // retryAttempts would silently bill a scoring stage twice.
  _taraFeedbackOpts() {
    return { timeout: 55000, isRetry: true, skipRetry: true };
  }

  async getTaraWritingFeedback(attemptId, questionDbId) {
    const query = questionDbId ? `?question_id=${encodeURIComponent(questionDbId)}` : '';
    return this.get(
      `/api/tara/writing-task/attempts/${encodeURIComponent(attemptId)}/scoring${query}`,
      this._taraFeedbackOpts(),
    );
  }

  async runTaraWritingStage(attemptId, stage, questionDbId) {
    return this.post(
      `/api/tara/writing-task/attempts/${encodeURIComponent(attemptId)}/scoring/${encodeURIComponent(stage)}`,
      questionDbId ? { question_id: questionDbId } : {},
      this._taraFeedbackOpts(),
    );
  }

  // Which papers have writing feedback, asked about papers rather than about
  // one sitting of them. Read-only and cheap, so it does not need the guards
  // the scoring calls have.
  async getTaraWritingFeedbackAvailability(paperIds) {
    return this.post('/api/tara/writing-task/papers/feedback-availability', {
      paper_ids: paperIds || [],
    });
  }

  // Same timeout/retry guards as TARA's, and the same reason: these calls
  // spend money, so a silently retried AbortError must not bill a scoring
  // stage twice.
  async getLnatSectionBFeedback(attemptId, questionDbId) {
    const query = questionDbId ? `?question_id=${encodeURIComponent(questionDbId)}` : '';
    return this.get(
      `/api/lnat/section-b/attempts/${encodeURIComponent(attemptId)}/scoring${query}`,
      this._taraFeedbackOpts(),
    );
  }

  async runLnatSectionBStage(attemptId, stage, questionDbId) {
    return this.post(
      `/api/lnat/section-b/attempts/${encodeURIComponent(attemptId)}/scoring/${encodeURIComponent(stage)}`,
      questionDbId ? { question_id: questionDbId } : {},
      this._taraFeedbackOpts(),
    );
  }

  async getLnatSectionBFeedbackAvailability(paperIds) {
    return this.post('/api/lnat/section-b/papers/feedback-availability', {
      paper_ids: paperIds || [],
    });
  }

  async markEssay(essayText, promptText) {
    return this.post('/api/quiz-generation/mark_essay', {
      essay_text: essayText,
      prompt_text: promptText
    });
  }
}

// Create and export quiz generator API instance
export const quizGeneratorAPI = new QuizGeneratorAPI();

